import React, { useEffect, useState } from "react";

/**
 * HeroCountdown — dramatic ticking clock ("The 48-hour mold window").
 *
 * Counts DOWN from `hours` to zero, then auto-resets. Persists the target
 * end-time in localStorage so refreshes don't restart the clock. When the
 * key changes (e.g., admin adjusts the hours), the clock resets to the new
 * duration.
 */
const pad = (n) => String(n).padStart(2, "0");

const HeroCountdown = ({ hours = 48, label }) => {
  const totalMs = Math.max(1, Number(hours) || 48) * 3600 * 1000;
  const storageKey = `atc-mold-countdown-${hours}`;

  const [remaining, setRemaining] = useState(() => {
    if (typeof window === "undefined") return totalMs;
    const saved = Number(window.localStorage.getItem(storageKey) || 0);
    const now = Date.now();
    if (saved && saved > now) return saved - now;
    const newEnd = now + totalMs;
    try { window.localStorage.setItem(storageKey, String(newEnd)); } catch { /* ignore */ }
    return totalMs;
  });

  useEffect(() => {
    const tick = () => {
      const end = Number(window.localStorage.getItem(storageKey) || 0) || (Date.now() + totalMs);
      const diff = end - Date.now();
      if (diff <= 0) {
        // Reset the loop
        const newEnd = Date.now() + totalMs;
        try { window.localStorage.setItem(storageKey, String(newEnd)); } catch { /* ignore */ }
        setRemaining(totalMs);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [storageKey, totalMs]);

  const hh = Math.floor(remaining / 3600000);
  const mm = Math.floor((remaining % 3600000) / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);

  return (
    <div
      className="inline-flex flex-col items-start gap-2"
      data-testid="hero-countdown"
    >
      <div className="flex items-center gap-3 rounded-2xl border border-atc-blue/30 bg-white/[0.04] backdrop-blur-xl px-5 py-3.5 shadow-[0_10px_40px_-10px_rgba(30,144,255,0.4)]">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-75" />
          <span className="relative rounded-full h-2.5 w-2.5 bg-rose-500" />
        </span>
        <div className="flex items-baseline gap-1.5 font-mono font-black tabular-nums text-white">
          <TimeCell value={pad(hh)} unit="hrs" />
          <span className="text-atc-blue/70 text-2xl sm:text-3xl -mt-1 animate-pulse">:</span>
          <TimeCell value={pad(mm)} unit="min" />
          <span className="text-atc-blue/70 text-2xl sm:text-3xl -mt-1 animate-pulse">:</span>
          <TimeCell value={pad(ss)} unit="sec" />
        </div>
      </div>
      {label && (
        <div
          className="text-xs sm:text-sm uppercase tracking-[0.22em] text-slate-300/90"
          data-testid="hero-countdown-label"
          dangerouslySetInnerHTML={{ __html: label }}
        />
      )}
    </div>
  );
};

const TimeCell = ({ value, unit }) => (
  <div className="flex flex-col items-center leading-none">
    <span className="text-2xl sm:text-3xl md:text-4xl">{value}</span>
    <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">{unit}</span>
  </div>
);

export default HeroCountdown;
